import React from 'react';
import { Tool, CheckCircle } from 'react-feather';

export interface ServiceMachineData {
  machine_id: number;
  machine_name: string;
  make_id: number;
  make_name: string;
  category_id?: number;
  /** customers_machines row id — unique per owned unit (machine_id is not). */
  customer_machine_id?: number | null;
  pfw_serial_number?: string | null;
}

interface ServiceMachineCardProps {
  machines: ServiceMachineData[];
  onSelect: (machine: ServiceMachineData) => void;
  disabled?: boolean;
  // Footer hint. Defaults to the chat copy; pass null to hide it (e.g. in the
  // form, where there is no chat to describe equipment in), or a custom node.
  hint?: React.ReactNode;
  // Currently-selected machine, to show which row is active (e.g. in the form's
  // prefill picker). The chat omits it, so its cards render unselected as before.
  selectedMachineId?: number | null;
}

const ServiceMachineCard: React.FC<ServiceMachineCardProps> = ({
  machines,
  onSelect,
  disabled = false,
  hint,
  selectedMachineId,
}) => {
  if (machines.length === 0) return null;

  return (
    <div className="service-request-card service-request-card--machines">
      <div className="service-request-card__header">
        <div className="service-request-card__icon service-request-card__icon--machine">
          <Tool size={20} />
        </div>
        <h3 className="service-request-card__title">Select Equipment</h3>
      </div>

      <div className="service-request-card__content">
        <p className="service-request-card__description">
          {machines.length === 1
            ? 'We found this equipment registered to your account:'
            : 'We found these pieces of equipment registered to your account. Which one needs service?'}
        </p>

        <div className="service-request-machines">
          {machines.map((machine) => {
            const isSelected =
              selectedMachineId != null && machine.machine_id === selectedMachineId;
            const select = () => {
              if (!disabled) onSelect(machine);
            };
            // The whole row is the click target (not just the pill) — people
            // instinctively click the machine name/icon, so a far-right button
            // alone reads as unresponsive.
            return (
              <div
                key={machine.customer_machine_id ?? machine.machine_id}
                className={
                  `service-request-machine` +
                  (isSelected ? ' is-selected' : '') +
                  (disabled ? ' is-disabled' : '')
                }
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-pressed={isSelected}
                aria-disabled={disabled || undefined}
                onClick={select}
                onKeyDown={(e) => {
                  if (disabled) return;
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    select();
                  }
                }}
              >
                <div className="service-request-machine__info">
                  <div className="service-request-machine__icon">
                    <Tool size={24} />
                  </div>
                  <div className="service-request-machine__details">
                    <div className="service-request-machine__make">{machine.make_name}</div>
                    <div className="service-request-machine__model">{machine.machine_name}</div>
                    {machine.pfw_serial_number && (
                      <div className="service-request-machine__serial">S/N: {machine.pfw_serial_number}</div>
                    )}
                  </div>
                </div>
                <span className={`service-request-machine__btn${isSelected ? ' is-selected' : ''}`}>
                  <CheckCircle size={16} />
                  {isSelected ? 'Selected' : 'Select'}
                </span>
              </div>
            );
          })}
        </div>

        {hint !== null && (
          <div className="service-request-card__hint">
            {hint ?? <span>Don't see your equipment? Describe it in the chat.</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceMachineCard;
